/**
 * Global Countdown Handler
 * Handles countdowns that are outside of web components
 */

export function initGlobalCountdowns() {
  console.log('🕐 Initializing global countdowns...')

  // Find all countdowns in the document that haven't been initialized yet
  const countdowns = document.querySelectorAll('[data-countdown]:not([data-countdown-initialized])')
  console.log('🕐 Found countdowns:', countdowns.length)

  if (countdowns.length === 0) {
    console.log('🕐 No uninitialized countdowns found')
    return
  }

  countdowns.forEach((countdown, index) => {
    // Mark as initialized to prevent double initialization
    countdown.setAttribute('data-countdown-initialized', 'true')

    const endDateString = countdown.dataset.end

    // Debug: Log the date string we're receiving
    console.log(`🕐 Countdown ${index + 1} - end date string:`, endDateString)

    if (!endDateString || endDateString.trim() === '') {
      console.error(`🕐 Countdown ${index + 1}: No end date provided`)
      return
    }

    // Parse date as local time
    // Format expected: "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD HH:MM"
    // JavaScript Date constructor can parse "YYYY-MM-DD HH:MM:SS" but interprets it as UTC
    // We need to parse it as local time
    let parsedDate
    try {
      // Strategy: Replace hyphens with slashes to force local time interpretation
      let localDateString = endDateString.trim()

      // If format is "YYYY-MM-DD HH:MM:SS", replace first two hyphens with slashes
      // This makes it "YYYY/MM/DD HH:MM:SS" which JavaScript interprets as local time
      if (localDateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        localDateString = localDateString.replace(
          /^(\d{4})-(\d{2})-(\d{2})/,
          '$1/$2/$3'
        )
      }

      parsedDate = new Date(localDateString)

      // Validate the date
      if (isNaN(parsedDate.getTime())) {
        console.error(
          `🕐 Countdown ${index + 1}: Invalid date format:`,
          endDateString
        )
        console.error(
          `🕐 Countdown ${index + 1}: Parsed string was:`,
          localDateString
        )
        return
      }
    } catch (error) {
      console.error(`🕐 Countdown ${index + 1}: Error parsing date:`, error)
      console.error(`🕐 Countdown ${index + 1}: Original string:`, endDateString)
      return
    }

    const endTime = parsedDate.getTime()

    // Debug: Log parsed date
    console.log(`🕐 Countdown ${index + 1} - Parsed end time:`, parsedDate)
    console.log(`🕐 Countdown ${index + 1} - Current time:`, new Date())
    console.log(
      `🕐 Countdown ${index + 1} - Time difference (ms):`,
      endTime - new Date().getTime()
    )
    console.log(
      `🕐 Countdown ${index + 1} - Time difference (hours):`,
      (endTime - new Date().getTime()) / (1000 * 60 * 60)
    )

    const elements = {
      days: countdown.querySelector('[data-countdown-days]'),
      daysContainer: countdown.querySelector('[data-countdown-days-container]'),
      hours: countdown.querySelector('[data-countdown-hours]'),
      minutes: countdown.querySelector('[data-countdown-minutes]'),
      seconds: countdown.querySelector('[data-countdown-seconds]'),
    }

    // Check if all required elements exist
    if (!elements.hours || !elements.minutes || !elements.seconds) {
      console.error(
        `🕐 Countdown ${index + 1}: Missing required countdown elements`
      )
      return
    }

    function updateCountdown() {
      const now = new Date().getTime()
      const distance = endTime - now

      if (distance <= 0) {
        if (elements.daysContainer) {
          elements.daysContainer.style.display = 'none'
        }
        if (elements.hours) elements.hours.textContent = '00'
        if (elements.minutes) elements.minutes.textContent = '00'
        if (elements.seconds) elements.seconds.textContent = '00'
        if (countdown._countdownInterval) {
          clearInterval(countdown._countdownInterval)
          countdown._countdownInterval = null
        }
        return
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      const hours = Math.floor((distance / (1000 * 60 * 60)) % 24)
      const minutes = Math.floor((distance / (1000 * 60)) % 60)
      const seconds = Math.floor((distance / 1000) % 60)

      if (elements.daysContainer) {
        elements.daysContainer.style.display = days > 0 ? 'flex' : 'none'
      }
      if (elements.days) {
        elements.days.textContent = String(days).padStart(2, '0')
      }
      if (elements.hours) {
        elements.hours.textContent = String(hours).padStart(2, '0')
      }
      if (elements.minutes) {
        elements.minutes.textContent = String(minutes).padStart(2, '0')
      }
      if (elements.seconds) {
        elements.seconds.textContent = String(seconds).padStart(2, '0')
      }

      // Show the countdown with smooth transition after updating values
      requestAnimationFrame(() => {
        countdown.classList.remove('opacity-0')
        countdown.classList.add('opacity-100')
      })
    }

    // Initial update
    updateCountdown()

    // Set up interval
    const interval = setInterval(updateCountdown, 1000)

    // Store interval on the element for potential cleanup
    countdown._countdownInterval = interval
  })
}

// Initialize when DOM is ready, but also wait a bit to ensure all content is loaded
function initializeCountdowns() {
  // Use requestAnimationFrame to ensure DOM is fully rendered
  requestAnimationFrame(() => {
    // Small delay to ensure all Liquid variables are rendered
    setTimeout(() => {
      initGlobalCountdowns()
    }, 100)
  })
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCountdowns)
} else {
  // DOM is already ready
  initializeCountdowns()
}

// Also re-initialize on Shopify section loads (for theme editor)
if (typeof window.Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', () => {
    // Remove initialization markers to allow re-initialization
    document.querySelectorAll('[data-countdown-initialized]').forEach((el) => {
      el.removeAttribute('data-countdown-initialized')
    })
    setTimeout(initGlobalCountdowns, 100)
  })
}
